import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Building2, Pencil, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  leadId: string;
  value: string[] | null;
}

/**
 * Lead-level list of cities the programme runs in. Activities with a blank or
 * country-only location are filled automatically (hotel-stay city first, then
 * these cities) by a database trigger; saving here re-applies it to the lead.
 */
export function ProgrammeCitiesField({ leadId, value }: Props) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(value || []);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: cityNames = [] } = useQuery({
    queryKey: ["all-city-names"],
    enabled: editing,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("cities").select("name").order("name");
      return Array.from(new Set((data || []).map((c: any) => c.name as string)));
    },
  });

  const add = (name: string) => {
    const n = name.trim();
    if (!n || draft.some((d) => d.toLowerCase() === n.toLowerCase())) return;
    setDraft([...draft, n]);
    setText("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("leads")
        .update({ programme_cities: draft.length ? draft : null } as any)
        .eq("id", leadId);
      if (error) throw error;
      const { data: n } = await (supabase as any).rpc("normalise_lead_item_locations", { _lead_id: leadId });
      await qc.invalidateQueries({ queryKey: ["lead", leadId] });
      await qc.invalidateQueries({ queryKey: ["itinerary-items"] });
      toast.success(
        Number(n) > 0 ? `Programme cities saved. ${n} activity location${Number(n) === 1 ? "" : "s"} filled in.` : "Programme cities saved",
      );
      setEditing(false);
    } catch (e: any) {
      toast.error(e?.message || "Could not save programme cities");
    } finally {
      setSaving(false);
    }
  };

  const suggestions = text.trim()
    ? cityNames.filter((c) => c.toLowerCase().includes(text.trim().toLowerCase())).slice(0, 6)
    : [];

  return (
    <div className="flex items-start gap-2">
      <Building2 className="h-3.5 w-3.5 text-primary mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="text-muted-foreground">Programme cities</p>
          {!editing && (
            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => { setDraft(value || []); setEditing(true); }} aria-label="Edit programme cities">
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
        {!editing ? (
          <p className="font-medium">{value?.length ? value.join(", ") : <span className="text-muted-foreground font-normal">From hotel stays</span>}</p>
        ) : (
          <div className="space-y-2 mt-1">
            <div className="flex flex-wrap gap-1">
              {draft.map((c) => (
                <Badge key={c} variant="secondary" className="gap-1">
                  {c}
                  <button type="button" onClick={() => setDraft(draft.filter((d) => d !== c))} aria-label={`Remove ${c}`}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(text); } }}
              placeholder="Type a city, press Enter"
              className="h-8"
            />
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {suggestions.map((s) => (
                  <Button key={s} type="button" size="sm" variant="outline" className="h-6 text-xs" onClick={() => add(s)}>{s}</Button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={saving}>
                {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
